"Components and page for the trending dashboard and pivot charts";

import "../cap-styles.scss";
import "react-pivottable/pivottable.css";

import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CModal,
  CModalBody,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CTabs,
} from "@coreui/react";
import React, { useState } from "react";
import {
  useAllPOCRecords,
  useSubsetDetailedPOCRecords,
} from "./trendingServices";

import CIcon from "@coreui/icons-react";
import DataTable from "react-data-table-component";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import PivotTable from "react-pivottable/PivotTable";
import PivotTableUI from "react-pivottable/PivotTableUI";
const Plot = React.lazy(() => import("react-plotly.js"));
import PropTypes from "prop-types";
import TableRenderers from "react-pivottable/TableRenderers";
import createPlotlyRenderers from "react-pivottable/PlotlyRenderers";
import { freeSet } from "@coreui/icons";
import { useGetModelVersionByVersionId } from "src/views/models/ModelService";
import { determineLoginPlatformType } from "src/reusable/Functions";

const AnalysisChoices = ({ choices, choiceHandler, selectedChoice }) => {
  const handleClick = (option) => {
    choiceHandler(option);
  };

  return (
    <div>
      <CDropdown className="mt-2">
        <CDropdownToggle
          caret
          color="info"
          data-attr="cap-ai:select:analysis-choice"
        >
          {selectedChoice}
        </CDropdownToggle>
        <CDropdownMenu>
          {choices.map((option, idx) => (
            <CDropdownItem
              key={idx}
              trigger="click"
              data-attr={`cap-ai:menu:select-analysis-${option}`}
              onClick={() => {
                handleClick(option);
              }}
            >
              {option}
            </CDropdownItem>
          ))}
        </CDropdownMenu>
      </CDropdown>
    </div>
  );
};

AnalysisChoices.propTypes = {
  choices: PropTypes.array,
  choiceHandler: PropTypes.func,
  selectedChoice: PropTypes.string,
};

const PlotlyRenderers = createPlotlyRenderers(Plot);

const ChartWindow = ({ data, filteredDataCallback }) => {
  const ANALYSIS_CHOICES = {
    // define these to implement preconfigured pivottable/pivotchart configurations.  They are consumed by the react-pivottable components.
    "POC YOY Change": {
      description:
        "View changes in the counts of the POC criteria as they change year over year. The larger the relative count, the hotter the cell.",
      cols: ["year"],
      rows: ["perf_obj"],
      aggregatorName: "Count as Fraction of Columns",
      rendererName: "Table Heatmap",
    },
    "Aggregated POC Distribution": {
      description:
        "View the distribution of POC criteria by all time count. The criteria bars are broken up further by the count of related performance objectives.",
      cols: ["perf_obj"],
      rows: ["crit"],
      aggregatorName: "Count",
      rendererName: "Stacked Column Chart",
      colOrder: "value_z_to_a",
    },
    "All POC Distribution": {
      description:
        "All time counts of POC criteria and performance objectives.",
      cols: [],
      rows: ["perf_obj", "crit"],
      aggregatorName: "Count",
      rendererName: "Table Heatmap",
    },
  };

  const [chartState, setChartState] = useState();

  const handleDataFieldClick = (filters, pivotData) => {
    var allData = pivotData.props.data;

    // filter the dataset with the filters applied by pivot chart
    // pivot chart component only returns the filters that should be applied and the entire dataset, we need the source_uids to go back and get the details from the API
    Object.entries(filters).forEach(([key, value]) => {
      allData = allData.filter((row) => {
        return row[key] === value;
      });
      return allData;
    });

    const source_uids = allData.map((x) => x.source_uid);
    filteredDataCallback(source_uids);
  };

  return (
    <>
      <CTabs activeTab={Object.keys(ANALYSIS_CHOICES)[0]}>
        <CNav variant="tabs">
          {Object.keys(ANALYSIS_CHOICES).map((analysisChoice, idx) => {
            return (
              <CNavItem key={idx}>
                <CNavLink
                  data-tab={analysisChoice}
                  data-attr={`cap-ai:tab:chart-${analysisChoice}`}
                >
                  {analysisChoice}
                </CNavLink>
              </CNavItem>
            );
          })}
          <CNavItem>
            <CNavLink
              data-tab={"free-form-analysis"}
              data-attr="cap-ai:tab:chart-free-form-analysis"
            >
              Free Form Analysis
            </CNavLink>
          </CNavItem>
        </CNav>
        <CTabContent>
          {Object.keys(ANALYSIS_CHOICES).map((analysisChoice, idx) => {
            return (
              <CTabPane
                style={{ padding: "10px" }}
                key={idx}
                data-tab={analysisChoice}
              >
                <div style={{ margin: "16px 0 16px 0" }}>
                  {ANALYSIS_CHOICES[analysisChoice].description}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <PivotTable
                    data={data}
                    onChange={(s) => setChartState(s)}
                    renderers={Object.assign(
                      {},
                      TableRenderers,
                      PlotlyRenderers,
                    )}
                    {...chartState}
                    {...ANALYSIS_CHOICES[analysisChoice]}
                    tableOptions={{
                      clickCallback: (e, values, filters, pivotData) => {
                        handleDataFieldClick(filters, pivotData);
                      },
                    }}
                  />
                </div>
              </CTabPane>
            );
          })}
          <CTabPane style={{ padding: "10px" }} data-tab="free-form-analysis">
            <PivotTableUI
              data={data}
              onChange={(s) => setChartState(s)}
              renderers={Object.assign({}, TableRenderers, PlotlyRenderers)}
              {...chartState}
              tableOptions={{
                clickCallback: (e, values, filters, pivotData) => {
                  handleDataFieldClick(filters, pivotData);
                },
              }}
              unusedOrientationCutoff={Infinity}
            />
          </CTabPane>
        </CTabContent>
      </CTabs>
    </>
  );
};

ChartWindow.propTypes = {
  data: PropTypes.any,
  filteredDataCallback: PropTypes.func,
};

const PocTable = ({ data }) => {
  return (
    <DataTable
      dense
      pagination
      responsive
      data={data.map((d) => d.data)}
      columns={Object.keys(data[0].data).map((key) => {
        return {
          name: key,
          sortable: true,
          selector: (row) => {
            return row[key];
          },
        };
      })}
    />
  );
};

PocTable.propTypes = {
  data: PropTypes.any,
};

export const TrendCodingProduct = ({
  datasetId,
  modelVersionId,
  topN,
  statsParams,
}) => {
  const [filteredSourceUids, setFilteredSourceUids] = useState();

  const { data: model, modelIsLoading } =
    useGetModelVersionByVersionId(modelVersionId);

  const { data: pocRecords, isLoading: pocRecordsLoading } = useAllPOCRecords(
    datasetId,
    model.id,
    model.versions[0].version_number,
    topN,
    //"timeSliceColumn":"dataset.condition_description" - take 2nd as col name
    statsParams.timeSliceColumn.split(".")[1],
  );

  const { data: subsetPocRecords, isLoading: subssetPocRecordsLoading } =
    useSubsetDetailedPOCRecords(datasetId, filteredSourceUids);

  return (
    <div style={{ margin: "0 16px 0 16px", minHeight: "300px" }}>
      <LoadingOverlay
        active={pocRecordsLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="This may take a moment. Loading Records..."
        styles={{ wrapper: { minHeight: "400px" } }}
      >
        <div>
          {!modelIsLoading && !pocRecordsLoading ? (
            <ChartWindow
              data={pocRecords}
              filteredDataCallback={(x) => setFilteredSourceUids(x)}
            ></ChartWindow>
          ) : null}
        </div>
        <div>
          <LoadingOverlay
            active={subssetPocRecordsLoading}
            spinner={<Loading height={"75em"} width={"75em"} />}
            text="Loading Drilldown Records..."
            styles={{ wrapper: { minHeight: "00px" } }}
          >
            {subsetPocRecords && <PocTable data={subsetPocRecords}></PocTable>}
          </LoadingOverlay>
        </div>
      </LoadingOverlay>
    </div>
  );
};

TrendCodingProduct.propTypes = {
  datasetId: PropTypes.number.isRequired,
  modelVersionId: PropTypes.number,
  topN: PropTypes.number,
  statsParams: PropTypes.object.isRequired,
};

export const AutomatedTrendCodingProductPatch = () => {
  return (
    <>
      <CContainer
        fluid
        style={{ marginTop: "1%" }}
        data-testid="cap-tc-container"
      >
        <CCard>
          <CCardHeader>
            <h3 data-testid="cap-tc-heading">
              Automate Your Trend Codes with {determineLoginPlatformType()}
            </h3>
          </CCardHeader>
          <CCardBody>
            <p>
              Automatically apply trend codes to your data using{" "}
              {determineLoginPlatformType()}, just follow the steps below!
            </p>
            <ol style={{ fontSize: "125%" }} data-testid="cap-tc-steps">
              <li>
                <p>
                  <a
                    href="/datasets"
                    target="_blank"
                    rel="opener"
                    data-testid="cap-tc-link-create-dataset"
                    data-attr="cap-ai:link:trend-coding-create-dataset"
                  >
                    Create a new dataset in {determineLoginPlatformType()}.
                  </a>
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-1.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "125px",
                  }}
                />
              </li>
              <li>
                <p>
                  Upload data to the dataset. Ensure you have one column
                  containing all the text you will feed into the trend coding
                  models.
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-2.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
              </li>
              <li>
                <p>
                  <a
                    href="/products/cap-ai"
                    target="_blank"
                    rel="opener"
                    data-testid="cap-tc-link-go-to-model"
                    data-attr="cap-ai:link:trend-coding-go-to-model"
                  >
                    Go to the model you would like to use
                  </a>
                  , view the model details, then associate and map the dataset
                  to the model.
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-3.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-4.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-5.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
              </li>
              <li>
                <p>
                  Navigate to the data analysis page, and score the dataset on
                  the model.
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-6.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-7.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "175px",
                  }}
                />
              </li>
            </ol>
          </CCardBody>
        </CCard>
      </CContainer>
    </>
  );
};

export const AutomatedTrendCodingProductPatchModalButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <>
      <CButton
        size="sm"
        style={{
          float: "right",
          paddingTop: "2px",
          paddingBottom: "2px",
        }}
        color="secondary"
        onClick={() => setIsVisible(true)}
        data-attr="cap-ai:btn:open-auto-trendcodes-tutorial"
      >
        <CIcon style={{ marginRight: "5px" }} content={freeSet.cilMediaPlay} />
        Auto Trendcodes Tutorial
      </CButton>
      <CModal
        show={isVisible}
        onClose={() => setIsVisible(false)}
        style={{ textAlign: "left" }}
        size="xl"
      >
        <CModalBody>
          <AutomatedTrendCodingProductPatch />
        </CModalBody>
      </CModal>
    </>
  );
};

export const TrendCodingProductPatch = () => {
  return (
    <>
      <CContainer
        fluid
        style={{ marginTop: "1%" }}
        data-testid="cap-td-container"
      >
        <CCard>
          <CCardHeader>
            <h3 data-testid="cap-td-heading">
              Analyze Your Trend Codes in Power BI
            </h3>
          </CCardHeader>
          <CCardBody>
            <p>
              Start analyzing your automated trend codes in Power BI! Perform
              the following steps to get started.
            </p>
            <ol style={{ fontSize: "125%" }} data-testid="cap-td-steps">
              <li>
                <p>
                  If you have not scored your data with trend codes,{" "}
                  <a
                    href="/products/cap-ai/automated-trend-coding"
                    target="_blank"
                    rel="opener"
                    data-testid="cap-td-link-tutorial"
                    data-attr="cap-ai:link:trending-dashboard-tutorial"
                  >
                    follow this tutorial first
                  </a>
                  .
                </p>
              </li>
              <li>
                <p>
                  <a
                    href="/datasets"
                    target="_blank"
                    rel="opener"
                    data-testid="cap-td-link-datasets"
                    data-attr="cap-ai:link:trending-dashboard-datasets"
                  >
                    Choose a dataset
                  </a>
                  , and navigate to the data analysis page.
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-6.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "125px",
                  }}
                />
              </li>
              <li>
                <p>
                  Select the options you would like to use for your analysis.
                </p>
                <ul style={{ fontSize: "80%" }}>
                  <li>
                    <p>
                      <strong>Model</strong> - This is the trend coding model
                      you will use the results from in the analysis.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Model Prediction</strong> - Choose the model
                      output to use. If the model only produces one type of
                      prediction, there will only be one option.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Limit Predictions</strong> - Limit the predictions
                      to use in the analysis. Models such as the PO&amp;C
                      labeler produce hundreds of codes for every record, but
                      generally only a few are useful for analysis. Limiting
                      predictions improves analysis, and reduces the run time.
                    </p>
                    <ul>
                      <li>
                        <p>
                          <strong>No</strong> - Use all predictions.
                        </p>
                      </li>
                      <li>
                        <p>
                          <strong>Top N Predictions</strong> - Only use the top
                          N predictions.
                        </p>
                      </li>
                      <li>
                        <p>
                          <strong>Predictions Above Probability</strong> - Only
                          use predictions above a certain confidence
                          probability.
                        </p>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <p>
                      <strong>How to aggregate predictions</strong> - Choose
                      whether to aggregate the predictions by the sum of the
                      confidences (recommended, as analysis will be more
                      accurate overall), or count.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Field to slice by</strong> <i>(optional)</i> -
                      Choose one or more fields to slice the data by. This is
                      useful for comparing trend codes across different
                      organizations, units, or sites.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Analze Slices by</strong> - Choose the statistic
                      to use to compare the different slices.
                    </p>
                    <ul>
                      <ul>
                        <li>
                          <p>
                            <strong>Relative Difference from Average</strong> -
                            Calculates the percentage difference from the
                            average value across all slices.
                          </p>
                        </li>
                        <li>
                          <p>
                            <strong>Absolute Difference from Average</strong> -
                            Calculates the absolute difference from the average
                            value across all slices.
                          </p>
                        </li>
                        <li>
                          <p>
                            <strong>Percentile</strong> - Calculates the
                            percentile of the value across all slices.
                          </p>
                        </li>
                        <li>
                          <p>
                            <strong>Standard Deviations from Mean</strong> -
                            Calculates the mean of all the other slices, then
                            calculates this values standard deviations from the
                            mean.
                          </p>
                        </li>
                      </ul>

                      <li>
                        <p>
                          <strong>
                            Use proportions instead of absolute counts for slice
                            stats
                          </strong>{" "}
                          - Uses the proportion of records within the time
                          period and slice for analysis instead of the absolute
                          count. Use this option if there are large differences
                          in the total number of records between slices and are
                          interested in analyzing differences in proportion.
                        </p>
                      </li>
                      <li>
                        <p>
                          <strong>
                            Exclude the slice being analyzed from the group
                            average
                          </strong>{" "}
                          - Excludes the slice from the group average for
                          analysis. This is often useful when you are analyzing
                          small groups where one record can significantly affect
                          the average.
                        </p>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <p>
                      <strong>Analyze Across Time</strong> - If you have a date
                      field in your data, choose whether to analyze the trend
                      codes across time. You can aggregate by day, month,
                      quarter or year, and run any statistics. Outlier analysis
                      can be run by using &quot;Outlier Analysis Standard
                      Deviations from Mean&quot; with time analysis.
                    </p>
                    <ul>
                      <li>
                        <p>
                          <strong>
                            Include first time period/last time period
                          </strong>{" "}
                          - Includes the first or last time period in the
                          analysis. If first or last time periods are
                          incomplete, it may be useful to unselect this option.
                        </p>
                      </li>
                      <li>
                        <p>
                          <strong>
                            Use proportions instead of absolute counts for to,e
                            stats
                          </strong>{" "}
                          - Uses the proportion of records within the time
                          period and slice for analysis instead of the absolute
                          count. Use this option if there are large differences
                          in the total number of records between time periods
                          and are interested in analyzing differences in
                          proportion.
                        </p>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <p>
                  Once you have selected your options, you can select any of the
                  buttons at the bottom of the page.
                </p>
                <img
                  src={
                    "/images/product-photos/tutorials/trenddashboardtutorial-9.png"
                  }
                  className="tutorial-img"
                  style={{
                    height: "50px",
                  }}
                />
                <ul>
                  <li>
                    <p>
                      <strong>View Analysis</strong> - Run the analysis and view
                      results in the browser.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Analysis CSV</strong> - Run the analysis and
                      download the results in a csv file.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Analysis JSON</strong> - Run the analysis and view
                      the raw JSON results.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Power BI</strong> - Pull the results into Power BI
                      for further analysis.
                    </p>
                  </li>

                  <li>
                    <p>
                      <strong>View Pivots</strong> - View select analysis
                      results in a pivot chart format.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Raw Predictions CSV</strong> - Download the raw
                      predictions in a csv file without any additional analysis.
                    </p>
                  </li>
                </ul>
              </li>
            </ol>
          </CCardBody>
        </CCard>
      </CContainer>
    </>
  );
};

export const TrendCodingProductPatchModalButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <>
      <CButton
        style={{
          float: "right",
          paddingTop: "2px",
          paddingBottom: "2px",
        }}
        color="secondary"
        onClick={() => setIsVisible(true)}
        size="sm"
        data-attr="cap-ai:btn:open-dataset-analytics-tutorial"
      >
        <CIcon style={{ marginRight: "5px" }} content={freeSet.cilMediaPlay} />
        Dataset Analytics Tutorial
      </CButton>
      <CModal
        show={isVisible}
        onClose={() => setIsVisible(false)}
        style={{ textAlign: "left" }}
        size="xl"
      >
        <CModalBody>
          <TrendCodingProductPatch />
        </CModalBody>
      </CModal>
    </>
  );
};
